/**
 * The admin app's controls, built from the tokens. They are smaller than the customer site's
 * `@lattice/ui` buttons on purpose: staff work through tables of records at a desk, so a 36px
 * control keeps a screen's worth of rows in view. On a touch screen every control grows to 44px.
 */
import * as stylex from "@stylexjs/stylex";
import type { MouseEvent, ReactNode } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { lastList, lastSearch, navigate } from "./router.ts";
const TOUCH = "@media (pointer: coarse)";

/* Dense desktop control height. Belongs to these controls only, so it is not a token. */
const CONTROL = "36px";
const focus = {
  ring: {
    kZs9gx: "x1jubnyg",
    kiEn40: "x7s97pk",
    kPJHz1: "x784prv",
    k7shQP: "x9v5kkp",
    $$css: true
  }
};
const styles = {
  link: {
    k8WAf4: "xp9eb12",
    kqGvvJ: "x17kn13p",
    kMwMTN: "x10dbofl x17m73gg",
    k63SB2: "xqu410z",
    kybGjl: "x1hl2dhg",
    kcSHmL: "xda060z",
    kaIpWk: "x8wwnw1",
    k67Jb4: "xt0b8zv",
    $$css: true
  },
  button: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x1173whf",
    kAzted: "xu0aao5 x17aqpur",
    kg3NbH: "x3i8z2n",
    kMv6JI: "x1cikjda",
    kGuDYH: "xl4dl4e",
    k63SB2: "xqu410z",
    kLWn49: "x1e5flnj",
    kaIpWk: "x1fbwzx",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x9r1u3d",
    kkrTdU: "x1ypdohk x1s07b3s",
    kSiTet: "x1hc1fzr x1t5pwa4",
    khDVqt: "xuxw1ft",
    ks0D6T: "x193iq5w",
    k1ekBW: "x1lxufdw",
    kIyJzY: "x149xavy",
    kAMwcw: "xa02eh3",
    k3aq6I: "x4e905j",
    $$css: true
  },
  primary: {
    kWkggS: "xmbr0q7 x1btvkoh",
    kMwMTN: "x1fb0272",
    $$css: true
  },
  danger: {
    kWkggS: "xtx2pbi x1btvkoh",
    kMwMTN: "x1fb0272",
    $$css: true
  },
  quiet: {
    kWkggS: "xcqm4px xi4h0sw",
    kVAM5u: "xtkjqb",
    kMwMTN: "xzb3lse",
    $$css: true
  },
  quietDanger: {
    kWkggS: "xcqm4px xi4h0sw",
    kVAM5u: "xtkjqb",
    kMwMTN: "x156x1qg",
    $$css: true
  },
  field: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xskm3c9",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  label: {
    kGuDYH: "xl4dl4e",
    k63SB2: "xqu410z",
    kMwMTN: "xzb3lse",
    $$css: true
  },
  hint: {
    kGuDYH: "xl4dl4e",
    kMwMTN: "x1jty25b",
    kogj98: "x1ghz6dp",
    $$css: true
  },
  input: {
    kB7OPa: "x9f619",
    kzqmXN: "xh8yej3",
    kAzted: "xu0aao5 x17aqpur",
    k8WAf4: "x1jyo4x1",
    kg3NbH: "xerji9v",
    kMv6JI: "x1cikjda",
    kGuDYH: "xl4dl4e x1vwvlh8",
    kLWn49: "xbz0q3o",
    kMwMTN: "xzb3lse",
    kWkggS: "xcqm4px",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xtkjqb",
    kaIpWk: "x1fbwzx",
    kZs9gx: "x1jubnyg",
    kiEn40: "x7s97pk",
    kPJHz1: "x784prv",
    k7shQP: "x9v5kkp",
    k1ekBW: "xshfolx",
    kIyJzY: "x149xavy",
    $$css: true
  },
  textarea: {
    kAzted: "x1r3qdcx",
    kHenm0: "x288g5",
    $$css: true
  },
  badge: {
    kGuDYH: "xl4dl4e",
    k63SB2: "xqu410z",
    kMwMTN: "xzb3lse",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  dot: {
    k1xSpc: "x1rg5ohu",
    kzqmXN: "xci0xqf",
    kZKoxP: "x1hagigm",
    k71WvV: "x15302su",
    kXLuUW: "xdnv9to",
    kaIpWk: "x1m3fsnl",
    $$css: true
  },
  okDot: {
    kWkggS: "x1gadm0y",
    $$css: true
  },
  dangerDot: {
    kWkggS: "xtx2pbi",
    $$css: true
  },
  warnDot: {
    kWkggS: "x862vy8",
    $$css: true
  },
  neutralDot: {
    kWkggS: "x1hgeuy3",
    $$css: true
  },
  fill: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  error: {
    kogj98: "x1ghz6dp",
    kGuDYH: "xl4dl4e",
    kMwMTN: "x156x1qg",
    k63SB2: "xqu410z",
    kHjlTd: "xj0a0fe",
    $$css: true
  }
};
export function Link({
  to,
  children
}: {
  readonly to: string;
  readonly children: ReactNode;
}) {
  function follow(event: MouseEvent<HTMLAnchorElement>) {
    /* A modified click opens a new tab, which the browser does better than this app could. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(to);
  }
  return <a href={to} onClick={follow} className="xp9eb12 x17kn13p x10dbofl x17m73gg xqu410z x1hl2dhg xda060z x8wwnw1 xt0b8zv x1jubnyg x7s97pk x784prv x9v5kkp">
      {children}
    </a>;
}

/** The way back from a detail page to the list it was opened from. */
export function BackToResults() {
  const query = lastSearch();
  return <Link to={lastList()}>
      {query === "" ? "Back to signups" : `Back to results for “${query}”`}
    </Link>;
}
export interface ButtonProps {
  readonly children: ReactNode;
  readonly tone?: "primary" | "danger" | "quiet" | "quietDanger";
  readonly type?: "button" | "submit";
  readonly disabled?: boolean;
  readonly fill?: boolean;
  readonly onClick?: () => void;
}
export function Button({
  children,
  tone = "primary",
  type = "button",
  disabled = false,
  fill = false,
  onClick
}: ButtonProps) {
  return <button type={type} disabled={disabled} onClick={onClick} {...stylex.props(styles.button, styles[tone], fill && styles.fill, focus.ring)}>
      {children}
    </button>;
}
export interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly hint?: string;
  readonly multiline?: boolean;
  readonly maxLength?: number;
  readonly type?: "text" | "email" | "search" | "password" | "number" | "date";
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
}
export function Select({
  id,
  label,
  value,
  onChange,
  options,
  hint
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly {
    readonly value: string;
    readonly label: string;
  }[];
  readonly hint?: string;
}) {
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  return <div className="x78zum5 xdt5ytf xskm3c9 xeuugli">
      <label htmlFor={id} className="xl4dl4e xqu410z xzb3lse">
        {label}
      </label>
      <select id={id} value={value} aria-describedby={hintId} onChange={event => onChange(event.target.value)} className="x9f619 xh8yej3 xu0aao5 x17aqpur x1jyo4x1 xerji9v x1cikjda xl4dl4e x1vwvlh8 xbz0q3o xzb3lse xcqm4px xmkeg23 x1y0btm7 xtkjqb x1fbwzx x1jubnyg x7s97pk x784prv x9v5kkp xshfolx x149xavy">
        <option value="">Choose</option>
        {options.map(option => <option key={option.value} value={option.value}>
            {option.label}
          </option>)}
      </select>
      {hint === undefined ? null : <p id={hintId} className="xl4dl4e x1jty25b x1ghz6dp">
          {hint}
        </p>}
    </div>;
}
export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  hint
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly {
    readonly value: string;
    readonly label: string;
  }[];
  readonly hint?: string;
}) {
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  return <div className="x78zum5 xdt5ytf xskm3c9 xeuugli">
      <label htmlFor={id} className="xl4dl4e xqu410z xzb3lse">
        {label}
      </label>
      <select id={id} value={value} aria-describedby={hintId} onChange={event => onChange(event.target.value)} className="x9f619 xh8yej3 xu0aao5 x17aqpur x1jyo4x1 xerji9v x1cikjda xl4dl4e x1vwvlh8 xbz0q3o xzb3lse xcqm4px xmkeg23 x1y0btm7 xtkjqb x1fbwzx x1jubnyg x7s97pk x784prv x9v5kkp xshfolx x149xavy">
        {options.map(option => <option key={option.value} value={option.value}>
            {option.label}
          </option>)}
      </select>
      {hint === undefined ? null : <p id={hintId} className="xl4dl4e x1jty25b x1ghz6dp">
          {hint}
        </p>}
    </div>;
}
export function Field(props: FieldProps) {
  const hintId = props.hint === undefined ? undefined : `${props.id}-hint`;
  const shared = {
    id: props.id,
    value: props.value,
    maxLength: props.maxLength,
    placeholder: props.placeholder,
    autoFocus: props.autoFocus,
    "aria-describedby": hintId
  };
  return <div className="x78zum5 xdt5ytf xskm3c9 xeuugli">
      <label htmlFor={props.id} className="xl4dl4e xqu410z xzb3lse">
        {props.label}
      </label>
      {props.multiline === true ? <textarea {...shared} onChange={event => props.onChange(event.target.value)} className="x9f619 xh8yej3 x1jyo4x1 xerji9v x1cikjda xl4dl4e x1vwvlh8 xbz0q3o xzb3lse xcqm4px xmkeg23 x1y0btm7 xtkjqb x1fbwzx x1jubnyg x7s97pk x784prv x9v5kkp xshfolx x149xavy x1r3qdcx x288g5" /> : <input {...shared} type={props.type ?? "text"} onChange={event => props.onChange(event.target.value)} className="x9f619 xh8yej3 xu0aao5 x17aqpur x1jyo4x1 xerji9v x1cikjda xl4dl4e x1vwvlh8 xbz0q3o xzb3lse xcqm4px xmkeg23 x1y0btm7 xtkjqb x1fbwzx x1jubnyg x7s97pk x784prv x9v5kkp xshfolx x149xavy" />}
      {props.hint === undefined ? null : <p id={hintId} className="xl4dl4e x1jty25b x1ghz6dp">
          {props.hint}
        </p>}
    </div>;
}
const DOTS = {
  ok: styles.okDot,
  danger: styles.dangerDot,
  warn: styles.warnDot,
  neutral: styles.neutralDot
};
export function Check({
  id,
  label,
  checked,
  onChange
}: {
  readonly id: string;
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return <label htmlFor={id} className="xl4dl4e xqu410z xzb3lse">
      <input id={id} type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} />{" "}
      {label}
    </label>;
}
export function Status({
  tone,
  children
}: {
  readonly tone: keyof typeof DOTS;
  readonly children: ReactNode;
}) {
  return <span className="xl4dl4e xqu410z xzb3lse xuxw1ft">
      <span {...stylex.props(styles.dot, DOTS[tone])} aria-hidden="true" />
      {children}
    </span>;
}
export function ErrorText({
  children
}: {
  readonly children: ReactNode;
}) {
  return <p role="alert" className="x1ghz6dp xl4dl4e x156x1qg xqu410z xj0a0fe">
      {children}
    </p>;
}